import type { FetchResponseError } from "../defaults";
import { HTTP_METHOD, ResponseError } from "../defaults";
import type { IErrorResponseProblem, ResponseErrorPayload } from "../payload";

import { getTextFromHTML } from "./utils";

export const ABORTED_CODE = 20;
export const ABORTED_NAME = "AbortError";
export const EMPTY_BODY_MESSAGE = "Response body is empty";
export const PARSING_ERROR_MESSAGE = "Failed parsing error, allegedly it's json, but it fails to parse";

const JSON_CONTENT_TYPE = "application/json";
const PROBLEM_PLUS_JSON_CONTENT_TYPE = "application/problem+json";

const isJsonContentType = (contentType: string) =>
    contentType.includes(JSON_CONTENT_TYPE) || contentType.includes(PROBLEM_PLUS_JSON_CONTENT_TYPE);

const getErrorProblemData = (errorResponse: object): IErrorResponseProblem | undefined => {
    const problem: IErrorResponseProblem = {};

    if ("Detail" in errorResponse && typeof errorResponse.Detail === "string") {
        problem.detail = errorResponse.Detail;
    }
    if ("Title" in errorResponse && typeof errorResponse.Title === "string") {
        problem.title = errorResponse.Title;
    }
    if ("Type" in errorResponse && typeof errorResponse.Type === "string") {
        problem.type = errorResponse.Type;
    }

    return Object.keys(problem).length > 0 ? problem : undefined;
};

const extractErrorFromResponse = async (response: Response | null) => {
    // We need to check if the response is null, because in case of an aborted request the response is null
    if (!response || response?.status === ABORTED_CODE) {
        return {
            name: ABORTED_NAME,
            status: ABORTED_CODE,
        };
    }
    // if BE sends a custom error message it might be a json we need to parse, or an empty body
    // Clone the response to be able to read it multiple times
    const errorResponse = response?.clone();
    const contentType = errorResponse?.headers.get("content-type");
    const contentLength = errorResponse?.headers.get("content-length");
    const contentLengthInt = typeof contentLength === "string" ? parseInt(contentLength, 10) : 0;

    let errorMessage: string | object;

    if (contentType && isJsonContentType(contentType) && contentLengthInt > 0) {
        try {
            errorMessage = await errorResponse?.json();
        } catch (parsingError) {
            const err = parsingError as Error;
            errorMessage = `${PARSING_ERROR_MESSAGE}: ${err?.name}: ${err?.message}`;
        }
    } else {
        try {
            errorMessage = (await errorResponse?.text()) || EMPTY_BODY_MESSAGE;
            errorMessage = getTextFromHTML(errorMessage);
        } catch (textParsingError) {
            console.error("textParsingError", textParsingError);
            const err = textParsingError as Error;
            errorMessage = `Exception thrown trying to parse text: ${err?.name}: ${err?.message}`;
        }
    }

    return {
        name: JSON.stringify(errorMessage),
        status: errorResponse?.status ?? 0,
        problem: typeof errorMessage !== "string" ? getErrorProblemData(errorMessage) : undefined,
    };
};

interface IGetResponseError {
    body?: object;
    error: FetchResponseError;
    method?: string;
    url: string;
}

export const getResponseError = async ({ body, error, method, url }: IGetResponseError) => {
    const { name, status, problem } = await extractErrorFromResponse(error.response);

    const errorPayload: ResponseErrorPayload = {
        problem,
        url,
        method,
        name,
        status,
    };

    if (method === HTTP_METHOD.post && status !== ABORTED_CODE) {
        //In case it's post we need to add extra params to the payload, except for aborted request
        errorPayload.body = body && JSON.stringify(body);
        errorPayload.err = JSON.stringify(error);
        errorPayload.response = JSON.stringify(error.response);
    }

    return new ResponseError(errorPayload);
};
